import { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SavingsIcon from '@mui/icons-material/Savings'
import SpeedIcon from '@mui/icons-material/Speed'
import { useComparison } from '../context/ComparisonContext'
import { useCorpusData } from '../context/CorpusDataContext'

function ComparisonCard({ item, onRemove, metrics, bestValues }) {
  if (!item) {
    return (
      <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', border: '2px dashed', borderColor: 'divider' }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography color="text.secondary">
            Add item to compare
          </Typography>
        </CardContent>
      </Card>
    )
  }

  const isCaseStudy = item.type === 'caseStudy'

  const formatValue = (key, value) => {
    if (value === null || value === undefined) return 'N/A'
    if (key === 'efficiency_pct') return `${value.toFixed(1)}%`
    if (key === 'annual_savings') return `$${value.toLocaleString()}`
    if (key === 'payback_months') return `${value} mo`
    if (key === 'kw_per_ton_before' || key === 'kw_per_ton_after') return value.toFixed(3)
    // Capitalize industry names
    if (key === 'industry' && typeof value === 'string') {
      return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }
    return value
  }

  const isBest = (key, value) => {
    if (!bestValues || !bestValues[key]) return false
    if (key === 'efficiency_pct' || key === 'annual_savings') {
      return value === bestValues[key] && value > 0
    }
    if (key === 'payback_months' || key === 'kw_per_ton_after') {
      return value === bestValues[key] && value > 0
    }
    return false
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Chip
              label={isCaseStudy ? 'Case Study' : 'ROI Scenario'}
              size="small"
              color={isCaseStudy ? 'primary' : 'secondary'}
              sx={{ mb: 1 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {item.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isCaseStudy ? item.data.source : item.data.equipmentType}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => onRemove(item.id, item.type)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        {metrics.map(({ key, label }) => {
          const value = isCaseStudy ? item.data[key] : item.data.results?.[key]
          const best = isBest(key, value)
          return (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1,
                bgcolor: best ? 'success.light' : 'transparent',
                borderColor: best ? 'success.main' : 'divider'
              }}
            >
              <Typography variant="caption" color={best ? 'success.dark' : 'text.secondary'}>
                {label}
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600 }}
                color={best ? 'success.dark' : 'text.primary'}
              >
                {formatValue(key, value)}
              </Typography>
            </Paper>
          )
        })}
      </CardContent>
    </Card>
  )
}

function Comparison() {
  const { items, removeItem, clearItems, addItem } = useComparison()
  const { caseStudies, loading, error } = useCorpusData()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [selectedCaseStudy, setSelectedCaseStudy] = useState('')

  // Auto-populate with 3 diverse studies when empty and data is loaded
  const [autoLoaded, setAutoLoaded] = useState(false)
  if (!autoLoaded && items.length === 0 && caseStudies && caseStudies.length > 0) {
    setAutoLoaded(true)
    // Pick 3 diverse studies: one CRAC, one RTU, one Chiller
    const picks = ['CRAC', 'RTU', 'Chiller']
      .map(type => caseStudies.find(s => s.equipment_type === type && s.efficiency_pct > 0))
      .filter(Boolean)
      .slice(0, 3)
    picks.forEach(study => {
      addItem({ id: study.id, type: 'caseStudy', data: study })
    })
  }

  const caseStudyItems = items.filter(i => i.type === 'caseStudy')
  const roiItems = items.filter(i => i.type === 'roi')

  const metrics = [
    { key: 'efficiency_pct', label: 'Efficiency Improvement' },
    { key: 'annual_savings', label: 'Annual Savings' },
    { key: 'payback_months', label: 'Payback Period' },
    { key: 'equipment_type', label: 'Equipment Type' },
    { key: 'industry', label: 'Industry' },
    { key: 'state', label: 'Location' }
  ]

  // Calculate best values for highlighting
  const bestValues = {}
  if (caseStudyItems.length > 1) {
    // Efficiency - higher is better
    const efficiencies = caseStudyItems.map(i => i.data.efficiency_pct).filter(v => v)
    if (efficiencies.length) bestValues.efficiency_pct = Math.max(...efficiencies)

    // Annual savings - higher is better
    const savings = caseStudyItems.map(i => i.data.annual_savings).filter(v => v)
    if (savings.length) bestValues.annual_savings = Math.max(...savings)

    // Payback - lower is better
    const paybacks = caseStudyItems.map(i => i.data.payback_months).filter(v => v)
    if (paybacks.length) bestValues.payback_months = Math.min(...paybacks)
  }

  const handleAddCaseStudy = () => {
    if (selectedCaseStudy && caseStudies) {
      const study = caseStudies.find(s => s.id === selectedCaseStudy)
      if (study) {
        addItem({
          id: study.id,
          type: 'caseStudy',
          data: study
        })
        setSelectedCaseStudy('')
      }
    }
  }

  const availableCaseStudies = (caseStudies || []).filter(
    s => !items.some(i => i.id === s.id && i.type === 'caseStudy')
  )

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Compare
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare up to 3 case studies or ROI scenarios side-by-side
          </Typography>
        </Box>
        {items.length > 0 && (
          <Button variant="outlined" color="error" onClick={clearItems}>
            Clear All
          </Button>
        )}
      </Box>

      {items.length === 0 ? (
        <Box>
          {/* Add studies controls */}
          <Card sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <CompareArrowsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="body1" sx={{ flex: 1, minWidth: 200 }}>
                Select up to 3 case studies to compare side-by-side
              </Typography>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>Select a Case Study</InputLabel>
                <Select
                  value={selectedCaseStudy}
                  label="Select a Case Study"
                  onChange={(e) => setSelectedCaseStudy(e.target.value)}
                >
                  {caseStudies.slice(0, 30).map(study => (
                    <MenuItem key={study.id} value={study.id}>
                      {study.id} — {study.equipment_type} — {study.efficiency_pct?.toFixed(1)}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleAddCaseStudy}
                disabled={!selectedCaseStudy}
              >
                Add to Compare
              </Button>
            </Box>
          </Card>

          {/* Example comparison preview */}
          <Typography variant="overline" sx={{ color: 'text.secondary', mb: 1.5, display: 'block', letterSpacing: '0.1em' }}>
            Example Comparison Preview
          </Typography>
          <Grid container spacing={3} sx={{ opacity: 0.55, pointerEvents: 'none' }}>
            {[
              { id: 'CS_001', type: 'CRAC', industry: 'Telecom', eff: 29.3, savings: 16455, kw: '1.50 → 1.06' },
              { id: 'CS_007', type: 'RTU', industry: 'Telecom', eff: 21.4, savings: 13422, kw: '1.53 → 1.20' },
              { id: 'CS_004', type: 'Chiller', industry: 'Commercial', eff: 16.5, savings: 50934, kw: '1.76 → 1.47' }
            ].map((ex) => (
              <Grid item xs={12} md={4} key={ex.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Chip label="Case Study" size="small" color="primary" sx={{ mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{ex.id}</Typography>
                        <Typography variant="body2" color="text.secondary">{ex.type} — {ex.industry}</Typography>
                      </Box>
                    </Box>
                    {[
                      { icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, label: 'Efficiency', value: `${ex.eff}%`, best: ex.eff >= 29 },
                      { icon: <SavingsIcon sx={{ fontSize: 16 }} />, label: 'Annual Savings', value: `$${ex.savings.toLocaleString()}`, best: ex.savings >= 50000 },
                      { icon: <SpeedIcon sx={{ fontSize: 16 }} />, label: 'kW/Ton', value: ex.kw, best: false }
                    ].map((m) => (
                      <Paper
                        key={m.label}
                        variant="outlined"
                        sx={{
                          p: 1.5, mb: 1,
                          bgcolor: m.best ? 'success.light' : 'transparent',
                          borderColor: m.best ? 'success.main' : 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {m.icon}
                          <Typography variant="caption" color={m.best ? 'success.dark' : 'text.secondary'}>{m.label}</Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }} color={m.best ? 'success.dark' : 'text.primary'}>
                          {m.value}
                        </Typography>
                      </Paper>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center', mt: 2 }}>
            Add case studies above to see your own comparison
          </Typography>
        </Box>
      ) : (
        <>
          {/* Add more items */}
          {items.length < 3 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography>Add more items to compare ({3 - items.length} slots remaining)</Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={selectedCaseStudy}
                    displayEmpty
                    onChange={(e) => setSelectedCaseStudy(e.target.value)}
                  >
                    <MenuItem value="" disabled>Select case study...</MenuItem>
                    {availableCaseStudies.slice(0, 20).map(study => (
                      <MenuItem key={study.id} value={study.id}>
                        {study.id} - {study.equipment_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddCaseStudy}
                  disabled={!selectedCaseStudy}
                >
                  Add
                </Button>
              </Box>
            </Alert>
          )}

          {/* Comparison Grid */}
          <Grid container spacing={4}>
            {items.map((item) => (
              <Grid item xs={12} md={4} key={`${item.type}-${item.id}`}>
                <ComparisonCard
                  item={item}
                  onRemove={removeItem}
                  metrics={metrics}
                  bestValues={bestValues}
                />
              </Grid>
            ))}
            {items.length < 3 && (
              <Grid item xs={12} md={4}>
                <ComparisonCard item={null} />
              </Grid>
            )}
          </Grid>

          {/* Summary */}
          {caseStudyItems.length > 1 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comparison Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Average Efficiency Improvement
                    </Typography>
                    <Typography variant="h5" color="primary.main" fontWeight={600}>
                      {(caseStudyItems.reduce((sum, i) => sum + (i.data.efficiency_pct || 0), 0) / caseStudyItems.length).toFixed(1)}%
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Total Annual Savings
                    </Typography>
                    <Typography variant="h5" color="success.main" fontWeight={600}>
                      ${caseStudyItems.reduce((sum, i) => sum + (i.data.annual_savings || 0), 0).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Average Payback
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {(caseStudyItems.filter(i => i.data.payback_months).reduce((sum, i) => sum + i.data.payback_months, 0) / caseStudyItems.filter(i => i.data.payback_months).length || 0).toFixed(1)} mo
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  )
}

export default Comparison
